/** Shared formatting and style lookups for the composer screen. */

import type {
  AssistantTurn,
  EscalationRequest,
  GenerationResult,
  RequestSpec,
  SocialAdSet,
} from "@/lib/api";

import type { GuardrailOutcome, ResolvedSpec, TargetLanguage } from "./types";

/**
 * Fill in every control so the UI never renders an undefined value.
 *
 * The fallbacks mirror the `RequestSpec` defaults on the backend; keeping them
 * here means a partially-parsed intake still produces a usable screen.
 */
export function resolveSpec(spec: RequestSpec | undefined | null): ResolvedSpec {
  return {
    target_language: spec?.target_language ?? "DE",
    audience: spec?.audience ?? "prospect",
    length: spec?.length ?? 5,
    framing: spec?.framing ?? "product_first",
    tone: spec?.tone ?? null,
  };
}

/**
 * Pull a human-readable message out of the `ApiError` thrown by the SDK.
 *
 * FastAPI returns `{ detail: string }` for handled errors and
 * `{ detail: [{ msg, loc }, …] }` for validation failures; both matter here
 * because "no materials supplied yet" arrives as the former.
 */
export function apiErrorMessage(error: unknown, fallback = "Something went wrong."): string {
  const body = error && typeof error === "object" ? (error as { body?: unknown }).body : undefined;
  const detail =
    body && typeof body === "object" ? (body as { detail?: unknown }).detail : undefined;

  if (typeof detail === "string" && detail.length > 0) {
    return detail;
  }
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) =>
        item && typeof item === "object" ? String((item as { msg?: unknown }).msg ?? "") : "",
      )
      .filter(Boolean);
    if (messages.length > 0) {
      return messages.join("; ");
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}

export const LANGUAGE_NAMES: Record<TargetLanguage, string> = {
  EN: "English",
  DE: "German",
  FR: "French",
  NL: "Dutch",
  DA: "Danish",
  IT: "Italian",
  ES: "Spanish",
  PL: "Polish",
};

/**
 * How each audience reads in a sentence — "written for architects".
 *
 * Shared by the ad copy preview and the copy draft: both are telling a market
 * who the words were aimed at, and two spellings of the same audience would
 * read as two different decisions.
 */
export const AUDIENCE_PHRASE: Record<string, string> = {
  architect: "architects",
  prospect: "prospects",
  homeowner: "homeowners",
};

export const OUTCOME_LABEL: Record<GuardrailOutcome, string> = {
  applied: "Applied",
  blocked: "Blocked",
  escalated: "Escalated",
};

export const OUTCOME_STYLE: Record<GuardrailOutcome, { row: string; icon: string; dot: string }> = {
  applied: {
    row: "border-outcome-applied/25 bg-outcome-applied-tint/60",
    icon: "text-outcome-applied",
    dot: "bg-outcome-applied",
  },
  blocked: {
    row: "border-outcome-blocked/30 bg-outcome-blocked-tint/70",
    icon: "text-outcome-blocked",
    dot: "bg-outcome-blocked",
  },
  escalated: {
    row: "border-outcome-escalated/30 bg-outcome-escalated-tint/70",
    icon: "text-outcome-escalated",
    dot: "bg-outcome-escalated",
  },
};

export function titleCase(value: string): string {
  return value
    .split(/[_\s]+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Collapse consecutive page numbers so "1,2,3,7" reads as "1–3, 7". */
export function formatPageRanges(pages: number[]): string {
  const sorted = [...pages].sort((a, b) => a - b);
  const ranges: string[] = [];
  let start: number | null = null;
  let previous: number | null = null;

  const flush = () => {
    if (start === null || previous === null) {
      return;
    }
    ranges.push(start === previous ? `${start}` : `${start}–${previous}`);
  };

  for (const page of sorted) {
    if (start === null || previous === null) {
      start = page;
      previous = page;
      continue;
    }
    if (page === previous + 1) {
      previous = page;
      continue;
    }
    flush();
    start = page;
    previous = page;
  }
  flush();
  return ranges.join(", ");
}

/** Flatten a tier-3 brief into the plain text a market would paste into a ticket. */
export function escalationBriefText(escalation: EscalationRequest): string {
  return [
    `Escalation ${escalation.id} — tier 3 (source design file required)`,
    `Asset: ${escalation.asset_filename} (${escalation.asset_id})`,
    `Location: ${escalation.location}`,
    `Element: ${escalation.element}`,
    `Requested change: ${escalation.requested_change}`,
    `Reason: ${escalation.reason}`,
    `Target language: ${escalation.target_language}`,
    `Deadline: ${escalation.deadline}`,
    `Status: ${escalation.status ?? "drafted"}`,
  ].join("\n");
}

/**
 * A short label for a brief, used as its entry in the rail.
 *
 * Built from the asset and the element it touches, because that is how someone
 * recognises their own request in a list — "the innovation house cover", not
 * "esc-e780ef6b".
 */
export function escalationTitle(escalation: EscalationRequest): string {
  // A request for something the library does not hold has no asset to name, so
  // it is named by what was asked for. Naming it from the placeholder filename
  // produced "— no matching asset — — A new asset", which named nothing.
  if (escalation.kind === "missing_asset" || escalation.asset_id === "none") {
    return trimToWords(escalation.requested_change, 7) || "A new asset";
  }
  const asset = titleCaseWords(
    escalation.asset_filename
      .replace(/\.[^.]+$/, "")
      .split(/[-_\s]+/)
      .filter(Boolean),
  );
  const element = escalation.element.trim();
  return element ? `${asset} — ${element}` : asset;
}

/**
 * The subject line for the request, on its own.
 *
 * Separate from the body because that is how it gets used: a market copies the
 * subject into their mail client's subject field and the body into the message.
 * One combined block meant deleting the "Subject:" line by hand every time.
 */
export function escalationEmailSubject(escalation: EscalationRequest): string {
  const prefix = escalation.kind === "missing_asset" ? "Asset request" : "Asset edit request";
  // Longer than the rail's title: a subject line has room, and "a photo of a
  // school gym with roof…" tells the reader less than the whole clause does.
  const subject =
    escalation.kind === "missing_asset"
      ? trimToWords(escalation.requested_change, 16)
      : escalationTitle(escalation);
  return `${prefix} — ${subject} (${escalation.target_language})`;
}

/** The first `limit` words of a phrase, marked as cut only when it was. */
function trimToWords(text: string, limit: number): string {
  const words = text.trim().split(/\s+/).filter(Boolean);
  return words.length > limit ? `${words.slice(0, limit).join(" ")}…` : words.join(" ");
}

/**
 * The body of the request, ready to paste under that subject.
 *
 * Written as a person would write it, with the structured fields kept inside
 * so it still pastes usefully into a ticket. The two escalation kinds are
 * genuinely different asks — an asset the library does not hold, or one that
 * needs a person's hands on it — and the opening line says which.
 */
export function escalationEmailBody(escalation: EscalationRequest): string {
  const missing = escalation.kind === "missing_asset";
  const opening = missing
    ? [
        "I'm looking for an asset the library doesn't hold yet, so I'd like to",
        "request it. Here's what I need it for:",
      ]
    : [
        "I need a change to an existing asset that the self-serve composer can't",
        "make on its own — it needs a manual touch:",
      ];

  const fields: Array<[string, string]> = missing
    ? [
        ["What I need", escalation.requested_change],
        ["Market / language", escalation.target_language],
        ["Needed by", escalation.deadline],
      ]
    : [
        ["Asset", escalation.asset_filename],
        ["Where", escalation.location],
        ["Element", escalation.element],
        ["Requested change", escalation.requested_change],
        ["Market / language", escalation.target_language],
        ["Needed by", escalation.deadline],
      ];

  const width = Math.max(...fields.map(([label]) => label.length)) + 2;

  return [
    "Hi Strategic Comms team,",
    "",
    ...opening,
    "",
    ...fields.map(([label, value]) => `${`${label}:`.padEnd(width)}${value}`),
    "",
    `Why it came to you: ${escalation.reason}`,
    "",
    `Reference: ${escalation.id}`,
    "",
    "Thanks!",
  ].join("\n");
}

/**
 * A short, human "when" for a remembered request.
 *
 * Today shows a clock time, anything older shows a date — the two questions a
 * person actually asks of history are "was that this morning?" and "was that
 * last week?".
 */
export function shortWhen(iso: string | undefined): string {
  if (!iso) {
    return "";
  }
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) {
    return "";
  }
  const now = new Date();
  const sameDay =
    when.getDate() === now.getDate() &&
    when.getMonth() === now.getMonth() &&
    when.getFullYear() === now.getFullYear();
  return sameDay
    ? when.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
    : when.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

// Noise that shows up in asset filenames but never helps name a request:
// photographer credits, size suffixes, export markers, bare ids.
const FILENAME_NOISE = new Set([
  "photo",
  "photos",
  "copy",
  "final",
  "draft",
  "v1",
  "v2",
  "by",
  "l",
  "h",
  "xh",
  "xl",
  "sm",
  "md",
  "lg",
  "web",
  "print",
  "rgb",
  "cmyk",
]);

function isNoiseToken(token: string): boolean {
  if (!token || FILENAME_NOISE.has(token)) {
    return true;
  }
  // Bare numbers and hex-looking ids — "6609542", "f0ec3", "cd8db".
  return /^\d+$/.test(token) || (/^[0-9a-f]{4,}$/.test(token) && /\d/.test(token));
}

const ACRONYMS = new Set([
  "LKR",
  "IH",
  "VELUX",
  "PDF",
  "PPT",
  "PPTX",
  "CO2",
  "EU",
  "UK",
  "US",
  "AI",
  "HQ",
  "LED",
  "DIY",
]);

function titleCaseWords(words: string[]): string {
  return words
    .map((word) => {
      // Length cannot tell VELUX from HOUSE, so acronyms are listed rather
      // than guessed. Everything else is a word that happened to be set in
      // caps in a PDF heading, and reads as shouting if left that way.
      const upper = word.toUpperCase();
      // A listed acronym is one however it was written — filenames carry them
      // lower-cased ("lkr-ih-case.pdf"), and "Lkr Ih Case" reads as a typo.
      if (ACRONYMS.has(upper)) {
        return upper;
      }
      const isAcronym = word === upper && upper.length <= 3 && /[A-Z]/.test(upper);
      return isAcronym ? word : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

/**
 * A short subject for an asset — what a person would call it.
 *
 * A document's own title is the best source, and PDFs carry one at the head of
 * their extracted text. Images do not, so their filename is cleaned instead:
 * the useful part is always the leading words, before the photographer, the id
 * and the size suffix.
 */
function assetSubject(filename: string | undefined, excerpt?: string | null): string {
  if (filename && /no matching/i.test(filename)) {
    return "";
  }
  // Only trust the excerpt when it opens on a heading. Extracted text starts
  // with whatever is largest on page one: sometimes a title set in caps
  // ("LKR INNOVATION HOUSE"), sometimes the first line of body copy ("A new
  // way to build..."), which names nothing. Caps is the signal that separates
  // them, and the filename is the better source when it is absent.
  const head = (excerpt ?? "").split(/[|.\n]/)[0]?.trim();
  if (head && head.length > 2) {
    const letters = head.replace(/[^A-Za-z]/g, "");
    const upperCount = head.replace(/[^A-Z]/g, "").length;
    const looksLikeHeading = letters.length > 0 && upperCount / letters.length >= 0.7;
    if (looksLikeHeading) {
      return titleCaseWords(head.split(/\s+/).slice(0, 3));
    }
  }
  if (!filename) {
    return "";
  }
  const stem = filename.replace(/\.[^.]+$/, "");
  const words: string[] = [];
  for (const token of stem.split(/[-_\s]+/)) {
    if (isNoiseToken(token.toLowerCase())) {
      // Stop at the first noise token once something real has been collected —
      // everything after it is metadata, not subject.
      if (words.length) {
        break;
      }
      continue;
    }
    words.push(token);
    if (words.length === 3) {
      break;
    }
  }
  return words.length ? titleCaseWords(words) : "";
}

const ARTEFACT_NOUN: Record<string, string> = {
  generate_deck: "deck",
  generate_social_ads: "social ads",
  propose_pdf_pages: "extract",
  localize_document: "translation",
  write_copy: "copy",
  asset_as_is: "asset",
  escalate_source_edit: "brief",
  needs_clarification: "request",
};

/**
 * A short, project-style name for a remembered request.
 *
 * The full sentence is unreadable in a list — ten near-identical requests look
 * the same at a glance. This names them the way someone would in conversation:
 * what it is about, what kind of thing it is, and what language it is in.
 */
export function requestTitle(turn: AssistantTurn): string {
  const plan = turn.deck_plan;
  const primary =
    plan?.assets_used?.find((asset) => asset.kind !== "image") ??
    plan?.assets_used?.[0] ??
    turn.asset ??
    undefined;

  // The searched candidates carry the full asset, excerpt included, so they
  // name a document better than a filename can — "LKR Innovation House" rather
  // than "Lkr Ih Case". Escalations and page picks only record a filename, so
  // this is where they get their title from.
  const searched = turn.candidates?.[0]?.asset;
  const subject =
    assetSubject(primary?.filename, primary?.excerpt) ||
    assetSubject(searched?.filename, searched?.excerpt) ||
    assetSubject(turn.document?.filename) ||
    assetSubject(turn.escalation_draft?.asset_filename);

  const noun = ARTEFACT_NOUN[turn.outcome] ?? "request";
  const language = turn.spec?.target_language;
  const inLanguage =
    language && language !== "EN" ? ` in ${LANGUAGE_NAMES[language] ?? language}` : "";

  if (!subject) {
    return `${noun.charAt(0).toUpperCase()}${noun.slice(1)}${inLanguage}`;
  }
  return `${subject} ${noun}${inLanguage}`;
}

/** One conversation: the request it opened with and every round since. */
export interface Conversation {
  /** The turn the conversation opened with — the row's stable identity. */
  rootId: string;
  /** Oldest first, the order the rounds were actually asked in. */
  turns: AssistantTurn[];
  /** Where the conversation got to, which is what opening it should show. */
  latest: AssistantTurn;
}

/** The thread's subject, with the parts that never tell two threads apart gone. */
function subjectKey(turn: AssistantTurn): string {
  return (turn.subject_text || turn.request_text).trim().replace(/\s+/g, " ").toLowerCase();
}

/**
 * Which turn each turn followed, read back out of what it was searched with.
 *
 * A saved turn records no parent id — the pointer lives on the *request*, and
 * the answer keeps it only in `subject_text`, which is the thread's subject
 * with each new round's words appended. So a turn's parent is the turn whose
 * subject its own subject begins with, and the longest such match is the
 * nearest ancestor. Reading the link off the text rather than an id also
 * survives the history collapsing repeated requests: when a middle round is
 * missing, the rounds around it still join the same conversation.
 */
function parentIds(turns: AssistantTurn[]): Map<string, string> {
  const keys = new Map(turns.map((turn) => [turn.turn_id, subjectKey(turn)]));
  const parents = new Map<string, string>();

  for (const turn of turns) {
    const key = keys.get(turn.turn_id) ?? "";
    let best: { id: string; length: number } | undefined;
    for (const other of turns) {
      const candidate = keys.get(other.turn_id) ?? "";
      // The space matters: it keeps "a deck on daylight" from adopting
      // "a deck on daylighting in schools", which is a different subject that
      // merely starts with the same letters.
      if (other.turn_id === turn.turn_id || !key.startsWith(`${candidate} `)) {
        continue;
      }
      if (!best || candidate.length > best.length) {
        best = { id: other.turn_id, length: candidate.length };
      }
    }
    if (best) {
      parents.set(turn.turn_id, best.id);
    }
  }
  return parents;
}

/** When a turn was asked, as a number that sorts. */
function askedAt(turn: AssistantTurn): number {
  const ms = Date.parse(turn.created_at);
  return Number.isNaN(ms) ? 0 : ms;
}

/**
 * Group a flat history into conversations, newest conversation first.
 *
 * Every ask is its own turn on the server, so a market that asks for a deck,
 * then "the same in French", then "make it shorter" produced three rows that
 * looked like three unrelated requests. They are one conversation with three
 * rounds, and this is what makes the rail able to say so.
 */
export function conversationsOf(turns: AssistantTurn[]): Conversation[] {
  const byId = new Map(turns.map((turn) => [turn.turn_id, turn]));
  const parents = parentIds(turns);
  // The list arrives newest first, so a later position means an older turn —
  // the tie-break for rounds that share a timestamp to the second.
  const position = new Map(turns.map((turn, index) => [turn.turn_id, index]));

  const rootOf = (turn: AssistantTurn): string => {
    // Visited ids, because the links are derived rather than given: a wrong
    // one could point back into its own chain, and a rail that hangs is worse
    // than a conversation shown twice.
    const seen = new Set<string>([turn.turn_id]);
    let current = turn.turn_id;
    for (;;) {
      const parent = parents.get(current);
      // A parent the fetch did not reach is not a parent we have — the window
      // is finite, so a thread's opening round can sit outside it. The round
      // stands as its own root rather than dropping out of the rail entirely.
      if (!parent || !byId.has(parent) || seen.has(parent)) {
        return current;
      }
      seen.add(parent);
      current = parent;
    }
  };

  const grouped = new Map<string, AssistantTurn[]>();
  for (const turn of turns) {
    const root = rootOf(turn);
    const existing = grouped.get(root);
    if (existing) {
      existing.push(turn);
    } else {
      grouped.set(root, [turn]);
    }
  }

  const conversations: Conversation[] = [];
  for (const [rootId, members] of grouped) {
    const ordered = [...members].sort(
      (a, b) =>
        askedAt(a) - askedAt(b) || (position.get(b.turn_id) ?? 0) - (position.get(a.turn_id) ?? 0),
    );
    const latest = ordered[ordered.length - 1];
    if (!latest) {
      continue;
    }
    conversations.push({ rootId, turns: ordered, latest });
  }

  return conversations.sort(
    (a, b) =>
      askedAt(b.latest) - askedAt(a.latest) ||
      (position.get(a.latest.turn_id) ?? 0) - (position.get(b.latest.turn_id) ?? 0),
  );
}

/** One file a conversation produced, treated as a version of that conversation. */
export interface ConversationVersion {
  turn: AssistantTurn;
  result: GenerationResult;
  /** Which round made it, counted from the conversation's opening request. */
  round: number;
}

/**
 * The files a conversation has produced, oldest first.
 *
 * Oldest first because that is the order they were versions in: English, then
 * the same in French, then the shorter cut. Rounds that made no file — a page
 * choice still waiting for an answer, an escalation — contribute nothing, so
 * this is a list of what exists rather than a row per ask.
 */
export function conversationVersions(
  conversation: Conversation | undefined,
): ConversationVersion[] {
  if (!conversation) {
    return [];
  }
  return conversation.turns
    .map((turn, index) => ({ turn, result: turn.result ?? undefined, round: index + 1 }))
    .filter((entry): entry is ConversationVersion => Boolean(entry.result));
}

/**
 * What tells two versions of one conversation apart.
 *
 * Not the filename: every round of a deck conversation generates a filename
 * built from the same subject, so a column of them reads as the same file
 * repeated. The round says which came first and the language says what changed.
 */
export function versionLabel(version: ConversationVersion): string {
  const language = version.result.spec.target_language;
  return `v${version.round} · ${language ? (LANGUAGE_NAMES[language] ?? language) : "—"}`;
}

/**
 * The one-line description of a file, in the words the conversation uses.
 *
 * Kept in step with the artifact row in `turn-card.tsx`, and short enough for
 * a rail: a translated Word file has neither slides nor kept pages, so each
 * kind is asked for the count it actually has.
 */
export function versionSummary(result: GenerationResult): string {
  const ads = socialAdsOf(result);
  if (ads) {
    // Several files, not one — counting its pages would say "0 pages".
    return `${ads.renders.length} ad${ads.renders.length === 1 ? "" : "s"}`;
  }
  if (result.output_kind === "pptx") {
    return `${(result.slides ?? []).length} slides`;
  }
  if (result.output_kind === "docx") {
    // Two different Word files end up here: a document translated in place,
    // and a set of written pieces. Counting the pieces is the only thing that
    // tells the second apart from the first in a one-line rail.
    const pieces = result.copy_draft?.pieces.length ?? 0;
    return pieces > 0 ? `${pieces} piece${pieces === 1 ? "" : "s"} of copy` : "Word document";
  }
  return `${(result.pages_kept ?? []).length} pages`;
}

/**
 * The rendered ads carried back by a confirm, if they came that way.
 *
 * Confirming formats returns the same envelope as every other build, and the
 * ad set itself lives on the turn — so the renders may arrive either on the
 * reloaded turn or alongside the file. Reading both means the grid appears the
 * moment the render finishes rather than after the history refetches.
 */
export function socialAdsOf(result: GenerationResult | undefined | null): SocialAdSet | undefined {
  return result?.social_ads ?? undefined;
}
